import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { nextTab, tabStop } from "../../tabNavigation";
import { Tabs as PrimitiveTabs, TabsList, TabsTrigger } from "../primitives/tabs";
import { useDesignSystem } from "./DesignSystem";
import "./selection.css";

type TabItem<T extends string> = { id: T; label: ReactNode; disabled?: boolean; testId?: string };

/** Controlled selection, ID-based focus and explicit cancellation are independent. */
export function Tabs<T extends string>({ idPrefix, label, value, items, onChange, className = "", variant = "line" }: {
  idPrefix: string; label: string; value: T; items: readonly TabItem<T>[];
  onChange: (value: T) => boolean | void; className?: string; variant?: "line" | "segmented";
}) {
  const root = useRef<HTMLElement>(null), nodes = useRef(new Map<T, HTMLButtonElement>());
  const themed = useDesignSystem();
  const focused = useRef<HTMLButtonElement | null>(null);
  const stop = tabStop(items, value);
  const revealTab = (id?: T) => {
    const target = id === undefined ? undefined : nodes.current.get(id);
    if (!target || !root.current) return;
    const row = target.getBoundingClientRect(), frame = root.current.getBoundingClientRect();
    // Reveal within the tab strip only, never scroll the page or another panel.
    // scrollLeft is rounded to device pixels; round outward so fractional
    // font widths cannot leave the selected tab's edge clipped.
    if (row.left < frame.left) root.current.scrollLeft = Math.floor(root.current.scrollLeft + row.left - frame.left);
    else if (row.right > frame.right) root.current.scrollLeft = Math.ceil(root.current.scrollLeft + row.right - frame.right);
  };
  const focusTab = (id?: T) => {
    const target = id === undefined ? root.current : nodes.current.get(id);
    target?.focus({ preventScroll: true });
    revealTab(id);
  };
  useLayoutEffect(() => {
    // Direct links, history and responsive resizing must reveal the active tab
    // without stealing focus or scrolling the surrounding page.
    revealTab(stop);
    const observer = new ResizeObserver(() => revealTab(stop));
    if (root.current) observer.observe(root.current);
    return () => observer.disconnect();
  }, [stop]);
  useLayoutEffect(() => {
    const previous = focused.current;
    if (previous && (!previous.isConnected || previous.disabled) && (document.activeElement === previous || document.activeElement === document.body)) focusTab(stop);
  }, [items, stop]);
  const choose = (id: T) => {
    if (!items.some(item => item.id === id && !item.disabled)) return;
    if (id !== value && onChange(id) === false) { focusTab(stop); return; }
    focusTab(id);
  };
  const keydown = (event: KeyboardEvent<HTMLButtonElement>, current: T) => {
    if (themed && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); choose(current); return; }
    const next = nextTab(items, current, event.key);
    if (next === undefined) return;
    event.preventDefault(); choose(next);
  };
  const nav = <nav ref={root} className={("ui-tabs ui-tabs--" + variant + " " + className).trim()} role="tablist" aria-label={label} tabIndex={-1}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) focused.current = null; }}>
    {items.map(item => {
      const button = <button key={item.id} type="button" role="tab" ref={node => { if (node) nodes.current.set(item.id, node); else nodes.current.delete(item.id); }}
      id={idPrefix + "-tab-" + item.id} data-tab-value={item.id} aria-selected={value === item.id}
      aria-controls={value === item.id ? idPrefix + "-panel-" + item.id : undefined}
      tabIndex={item.id === stop ? 0 : -1} disabled={item.disabled} data-testid={item.testId}
      onFocus={event => { focused.current = event.currentTarget; }}
      // Guarded selection belongs on click, after the browser's pointer phase.
      // Otherwise Radix's mousedown selection can restore focus on cancellation
      // only for the browser to move it back to the rejected tab immediately.
      onMouseDown={themed ? event => event.preventDefault() : undefined} onClick={() => choose(item.id)} onKeyDown={event => keydown(event, item.id)}>{item.label}</button>;
      return themed ? <TabsTrigger key={item.id} value={item.id} asChild>{button}</TabsTrigger> : button;
    })}
  </nav>;
  return themed ? <PrimitiveTabs value={value} onValueChange={id => choose(id as T)} activationMode="manual" asChild><TabsList asChild>{nav}</TabsList></PrimitiveTabs> : nav;
}
